import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useClerkAuth';
import { UserButton } from '@clerk/clerk-react';
import TopNavBar from '@/components/TopNavBar';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Settings as SettingsIcon,
  ArrowLeft,
  User,
  CreditCard,
  Bell,
  Shield,
  Globe,
  ExternalLink,
  Crown,
} from 'lucide-react';

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, userRole, hasProAccess } = useAuth();

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'EDITOR':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'READER':
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return <Shield className="h-3 w-3" />;
      case 'EDITOR':
        return <Crown className="h-3 w-3" />;
      case 'READER':
      default:
        return <User className="h-3 w-3" />;
    }
  };

  return (
    <>
      <TopNavBar
        currentPage="dashboard"
        onNavigateToAdmin={() => navigate('/?view=admin')}
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 p-6">
        <div className="container max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="bg-white/10 border-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('settings.backToDashboard')}
              </Button>
            </div>

            <div className="bg-gradient-hero rounded-lg p-6 text-primary-foreground mb-6">
              <div className="flex items-center gap-3 mb-2">
                <SettingsIcon className="h-8 w-8" />
                <h1 className="text-3xl font-bold">{t('settings.title')}</h1>
              </div>
              <p className="text-primary-foreground/90">
                {t('settings.subtitle')}
              </p>
            </div>
          </div>

          {/* Account Information */}
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('settings.accountInfo')}
              </CardTitle>
              <CardDescription>{t('settings.accountInfoDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {user?.primaryEmailAddress?.emailAddress}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleIcon(userRole)}
                    <Badge
                      variant="outline"
                      className={getRoleBadgeColor(userRole)}
                    >
                      {userRole}
                    </Badge>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">
                    {t('settings.rolePermissions')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="space-y-1">
                      <p className="font-medium text-green-700">
                        {t('settings.availablePermissions')}
                      </p>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>{t('settings.readArticles')}</li>
                        <li>{t('settings.practiceQuestions')}</li>
                        <li>{t('settings.trackProgress')}</li>
                      </ul>
                    </div>

                    {(userRole === 'EDITOR' || userRole === 'ADMIN') && (
                      <div className="space-y-1">
                        <p className="font-medium text-blue-700">
                          {t('settings.editorAccess')}
                        </p>
                        <ul className="space-y-1 text-muted-foreground">
                          <li>{t('settings.createArticles')}</li>
                          <li>{t('settings.editContent')}</li>
                          <li>{t('settings.manageQuestions')}</li>
                        </ul>
                      </div>
                    )}

                    {userRole === 'ADMIN' && (
                      <div className="space-y-1">
                        <p className="font-medium text-red-700">
                          {t('settings.adminAccess')}
                        </p>
                        <ul className="space-y-1 text-muted-foreground">
                          <li>{t('settings.deleteContent')}</li>
                          <li>{t('settings.manageUsers')}</li>
                          <li>{t('settings.fullAccess')}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription */}
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5" />
                {t('settings.subscription')}
              </CardTitle>
              <CardDescription>
                {t('settings.subscriptionDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {hasProAccess ? (
                  // Pro User - Show current subscription status
                  <div className="p-4 rounded-lg border bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Crown className="h-5 w-5 text-emerald-600" />
                          <h4 className="font-semibold text-emerald-900">
                            {t('settings.proActive')}
                          </h4>
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            {t('settings.proBadge')}
                          </Badge>
                        </div>
                        <p className="text-sm text-emerald-700">
                          {t('settings.proDesc')}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            {t('settings.unlimitedQuestions')}
                          </Badge>
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                            {t('settings.noRateLimits')}
                          </Badge>
                          <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                            {t('settings.premiumFeatures')}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate('/pricing')}
                        variant="outline"
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      >
                        {t('settings.manageSubscription')}
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Free User - Show upgrade options
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Crown className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold text-purple-900">
                          {t('settings.upgradeTitle')}
                        </h4>
                      </div>
                      <p className="text-sm text-purple-700">
                        {t('settings.upgradeDesc')}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                          {t('settings.unlimitedQuestions')}
                        </Badge>
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                          {t('settings.noRateLimits')}
                        </Badge>
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          {t('settings.premiumFeatures')}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate('/pricing')}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    >
                      {t('settings.viewPricing')}
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}

                <div className="text-xs text-muted-foreground bg-gray-50 p-3 rounded-lg border">
                  <p className="font-medium text-gray-800 mb-1">
                    {t('settings.currentPlan')}{' '}
                    {hasProAccess
                      ? t('settings.planPro')
                      : t('settings.planFree')}
                  </p>
                  <p>
                    {hasProAccess
                      ? t('settings.proDescription')
                      : t('settings.freeDescription')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Management */}
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {t('settings.accountManagement')}
              </CardTitle>
              <CardDescription>
                {t('settings.accountManagementDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border bg-gray-50/50">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {t('settings.profileSettings')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.profileSettingsDesc')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <UserButton
                      appearance={{
                        elements: {
                          avatarBox: 'w-10 h-10',
                          userButtonPopover: 'shadow-lg',
                        },
                      }}
                      userProfileMode="modal"
                    />
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                <div className="text-xs text-muted-foreground bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="font-medium text-blue-800 mb-1">
                    {t('settings.accessSettings')}
                  </p>
                  <p>{t('settings.accessInstructions')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t('settings.preferences')}
              </CardTitle>
              <CardDescription>{t('settings.preferencesDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">{t('settings.language')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.languagePreference')}
                    </p>
                  </div>
                  <Badge variant="outline">
                    {t('settings.englishDefault')}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">{t('settings.notifications')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.notificationsDesc')}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-yellow-100 text-yellow-800 border-yellow-200"
                  >
                    <Bell className="h-3 w-3 mr-1" />
                    {t('settings.comingSoon')}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium">{t('settings.learningGoals')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.learningGoalsDesc')}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-yellow-100 text-yellow-800 border-yellow-200"
                  >
                    <Bell className="h-3 w-3 mr-1" />
                    {t('settings.comingSoon')}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Help & Support */}
          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t('settings.helpAndSupport')}
              </CardTitle>
              <CardDescription>{t('settings.helpAndSupport')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">
                      {t('settings.applicationVersion')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.appVersion')}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium">{t('settings.needHelp')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.contactSupport')}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Settings;
